import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import { RetailerNetworkLocationFilter } from '@occc/graphql';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';

import { LocationList } from './location-list.component';
import { AFTER_PARAM_NAME, BEFORE_PARAM_NAME } from './retailer-network-locations.constants';

import { useRemoveLocationFromRetailerNetworkMutation } from '../../../../../data-access/mutations';
import { useGetRetailerNetworkLocations } from '../../../../../data-access/queries';
import { RemoveLocationFromRetailerNetworkDialog } from '../../../../retailer-network-locations-configuration/retailer-network-locations/remove-location-from-retailer-network-dialog.component';
import { routeCreator } from '../../../../retailer-connect.utils';

type LocationListContainerProps = ReturnType<typeof useBulkSelect> & {
  filter: RetailerNetworkLocationFilter;
  handleOpenAddLocationsToLocationGroupDialog: () => void;
  retailerNetworkUuid: string;
};

export const LocationListContainer = ({
  filter,
  bulkSelectedItems,
  handleBulkSelectMultiple,
  handleBulkDeselectAll,
  handleItemSelect,
  handleOpenAddLocationsToLocationGroupDialog,
  retailerNetworkUuid,
}: LocationListContainerProps) => {
  const [removeLocationDialogOpen, setRemoveLocationDialogOpen] = useState<boolean>(false);
  const [locationToRemoveUuid, setLocationToRemoveUuid] = useState<string | null>(null);

  const navigate = useNavigate();

  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: BEFORE_PARAM_NAME,
    afterParamName: AFTER_PARAM_NAME,
  });

  const locationsData = useGetRetailerNetworkLocations({
    filter,
    paginationInfo,
    onPageChange,
  });

  const onCompleted = () => {
    if (locationToRemoveUuid) {
      if (bulkSelectedItems.has(locationToRemoveUuid)) {
        handleItemSelect(locationToRemoveUuid);
      }

      setLocationToRemoveUuid(null);
    }

    setRemoveLocationDialogOpen(false);
    snackbar.success('Location were removed from the Retailer Network.');
  };
  const onError = () => {
    snackbar.error('Unable to remove Location from the Retailer Network. Please try again later.');
  };

  const [
    removeLocationFromRetailerNetwork,
    { loading: isRemoveLocationFromRetailerNetworkInProgress },
  ] = useRemoveLocationFromRetailerNetworkMutation({
    onCompleted,
    onError,
    refetchQueries: ['retailerNetwork', 'retailerNetworkLocations'],
  });

  const onConfirm = () => {
    if (locationToRemoveUuid) {
      removeLocationFromRetailerNetwork({
        variables: {
          retailerNetworkUuid,
          locationUuid: locationToRemoveUuid,
        },
      });
    }
  };

  const onRemoveLocationMenuItemClick = (locationUuid: string) => {
    setRemoveLocationDialogOpen(true);
    setLocationToRemoveUuid(locationUuid);
  };

  const handleOpenLocationDetailsPage = (locationUuid: string) => {
    navigate(routeCreator.locationDetails(retailerNetworkUuid, locationUuid));
  };

  return (
    <>
      <LocationList
        locationsData={locationsData}
        bulkSelectedItems={bulkSelectedItems}
        handleBulkSelectMultiple={handleBulkSelectMultiple}
        handleBulkDeselectAll={handleBulkDeselectAll}
        handleItemSelect={handleItemSelect}
        handleOpenAddLocationsToLocationGroupDialog={handleOpenAddLocationsToLocationGroupDialog}
        onRemoveLocationMenuItemClick={onRemoveLocationMenuItemClick}
        handleOpenLocationDetailsPage={handleOpenLocationDetailsPage}
        retailerNetworkUuid={retailerNetworkUuid}
      />
      <RemoveLocationFromRetailerNetworkDialog
        open={removeLocationDialogOpen}
        setOpen={setRemoveLocationDialogOpen}
        onConfirm={onConfirm}
        isRemoveLocationFromRetailerNetworkInProgress={
          isRemoveLocationFromRetailerNetworkInProgress
        }
      />
    </>
  );
};
